/**
 * CardBackGlyph — the shared face-down "card back" emblem, authored in the same
 * compact code-native pixel format (a 9×9 grid indexing three colours).
 * Rendered through <AuthoredGlyph>, so it reuses the crisp-edges SVG pipeline
 * with no new asset system. Used by face-down board cards during the Marvel
 * Snap-style reveal (all cards flip/fade to their back, then reveal one by one).
 */
import type { ReactElement } from 'react';
import type { PixelGlyph } from '@cards/cards';
import { AuthoredGlyph } from './AuthoredGlyph.js';
import { PixelPattern, type PixelPatternDef } from './PixelPattern.js';
import { COLORS } from './kit/tokens.js';

/**
 * The back's mark (design system 01 §10): a gold diamond in an ink outline with
 * a felt heart, on the felt back; symmetric at any size.
 */
const CARD_BACK_GLYPH: PixelGlyph = {
  colors: [COLORS.outline, COLORS.cardBackMark, COLORS.cardBackPatternLight],
  rows: [
    '....1....',
    '...121...',
    '..12221..',
    '.1223221.',
    '122333221',
    '.1223221.',
    '..12221..',
    '...121...',
    '....1....',
  ],
};

/** The back's weave: the felt diamond lattice in the two lighter felt tones. */
export const CARD_BACK_PATTERN: PixelPatternDef = Object.freeze({
  colors: [COLORS.cardBackPattern, COLORS.cardBackPatternLight],
  rows: Object.freeze([
    '...2...',
    '..212..',
    '.21.12.',
    '21.1.12',
    '.21.12.',
    '..212..',
    '...2...',
  ]),
});

/** Shared tiled miniature of the central emblem used by every card back. */
export function CardBackPattern({
  id,
  testId,
  className,
}: Readonly<{
  id: string;
  testId?: string;
  className?: string;
}>): ReactElement {
  return (
    <PixelPattern
      pattern={CARD_BACK_PATTERN}
      id={id}
      cell={3}
      {...(testId === undefined ? {} : { testId })}
      {...(className === undefined ? {} : { className })}
    />
  );
}

export function CardBackGlyph({ size = 84 }: { size?: number }): ReactElement {
  return (
    <AuthoredGlyph
      glyph={CARD_BACK_GLYPH}
      testId="card-back"
      visualId="card-back"
      size={size}
    />
  );
}

/**
 * The back a face-down board card shows (02 §3.2): the weave and the gold
 * diamond. One markup for the board card, the reveal lineup and a dealt card in
 * flight, so the three hand over to one another without a changed frame.
 */
export function CardBackFace({
  patternId,
  className,
  hidden = false,
}: Readonly<{
  patternId: string;
  /** Extra class for a context (the flight's `cards-return-back`). */
  className?: string;
  /** `aria-hidden` on the back itself, where the card around it is not hidden. */
  hidden?: boolean;
}>): ReactElement {
  return (
    <div
      className={className === undefined ? 'cards-board-card-back' : `cards-board-card-back ${className}`}
      {...(hidden ? { 'aria-hidden': true } : {})}
    >
      <CardBackPattern id={patternId} className="cards-board-card-back-pattern" />
      <CardBackGlyph size={84} />
    </div>
  );
}
